// src/store/slices/auth/selectors.ts

import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

// ---- AUTH ROOT SELECTORS ----

/**
 * Root selector for the auth slice.
 * @param state - The root Redux state.
 * @returns The entire auth slice state.
 */
export const selectAuth = (state: RootState) => state.auth;

// ---- FLAT AUTH SELECTORS ----

/**
 * Selects the authentication status ('idle', 'loading', 'authenticated', 'error').
 */
export const selectAuthStatus = createSelector(selectAuth, state => state.status);

/**
 * Selects the current registration step (top-level field on auth).
 */
export const selectRegStep = createSelector(selectAuth, auth => auth.registrationStep);

/**
 * Selects the current login step ('credentials', 'choose2fa', etc).
 */
export const selectLoginStep = createSelector(selectAuth, auth => auth.loginStep);

/**
 * Checks if the user is authenticated.
 */
export const selectLoginStatus = createSelector(selectAuth, auth => auth.status === 'authenticated');

/**
 * Selects the selected 2FA method ('sms' | 'email' | undefined).
 */
export const selectTwoFactorMethod = createSelector(selectAuth, auth => auth.twoFactorMethod);

/**
 * Selects the top-level auth error string, if present.
 */
export const selectLoginError = createSelector(selectAuth, auth => auth.error);

/**
 * Returns true if an auth or registration-related form is currently submitting.
 */
export const selectIsSubmitting = createSelector(selectAuth, auth => auth.isSubmitting);

/**
 * Selects the current registration token, if any.
 */
export const selectRegToken = createSelector(selectAuth, auth => auth.regToken);

/**
 * Returns a selector for a specific field error by field name.
 * @param field - The field name.
 */
export const selectFieldError = (field: string) =>
  createSelector(selectAuth, auth => auth.fieldErrors[field]);

export const selectAuthFieldErrors = (state: RootState) => state.auth.fieldErrors;

// ---- REGISTRATION (NESTED UNDER AUTH) ----

/**
 * Root selector for the nested registration state under auth.
 */
export const selectRegistration = createSelector(
  selectAuth,
  auth => auth.registration
);

/**
 * Selects the current registration step (multi-step wizard).
 */
export const selectRegistrationStep = createSelector(
  selectRegistration,
  reg => reg.step
);

/**
 * Selects the entire registration data object (username, email, password, codes, etc).
 */
export const selectRegistrationData = createSelector(
  selectRegistration,
  reg => reg.data
);

/**
 * Returns true if the registration flow is loading.
 */
export const selectRegistrationLoading = createSelector(
  selectRegistration,
  reg => reg.loading
);

/**
 * Selects the registration error, if any.
 */
export const selectRegistrationError = createSelector(
  selectRegistration,
  reg => reg.error
);

/**
 * Returns an object with loading, error, and step for registration status at once.
 */
export const selectRegistrationStatus = createSelector(
  selectRegistration,
  reg => ({
    loading: reg.loading,
    error: reg.error,
    step: reg.step,
  })
);

/**
 * Shortcut to get the full registration state (all fields).
 */
export const selectRegistrationAll = selectRegistration;

export const selectUser = createSelector(selectAuth, (auth) => auth.user);

export const selectAuthError = createSelector(selectAuth, (auth) => auth.error);

export const selectUserId = createSelector(selectAuth, (auth) => auth.userId);

export const selectTwoFactorMaskedDestination = createSelector(selectAuth, authState => authState.twoFactorMaskedDestination);

export const selectMaskedEmail = createSelector(selectAuth, auth => auth.maskedEmail);

export const selectMaskedPhone = createSelector(selectAuth, auth => auth.maskedPhone);

export const selectForgotPasswordEmailAddress = createSelector(
  selectAuth,
  (authState) => authState.forgotPasswordEmailAddress
);

export const selectRecoveryToken = createSelector(
  selectAuth,
  (authState) => authState.recoveryToken
);

export const selectRecoveryMaskedPhone = createSelector(
  selectAuth,
  (authState) => authState.recoveryMaskedPhone
);